import {keepPreviousData, QueryClient, queryOptions, useQuery} from '@tanstack/react-query'
import { DateRange } from 'react-day-picker'

import * as apiClient from '@/lib/api'
import {useIdentity} from "@/lib/auth/hooks/identity"
import { formatDate } from '@/lib/filters/date/date-utils'
import useIsQueryEnabled from '@/lib/hooks/query-enabler'
import {useQueryResetWithRefresh} from "@/lib/hooks/query-refetch-with-reset"
import { ContentAddedOrSharedByAdviser, VerifiedByAdviserStats } from '@/modules/verified-by-adviser/types'
import { useDate } from '@/stores/date-filter'
import {FetchParams} from "@/types/search";
import { QUERY_KEY_CONTENT_ADDED_OR_SHARED_STATS, QUERY_KEY_VERIFIED_BY_ADVISER_STATS } from '@/utils/keys'

export const buildQueryParamsString = (fetchParams: FetchParams): string => {
  const queryParams = new URLSearchParams()

  queryParams.append("limit", fetchParams.limit.toString())
  queryParams.append("offset", fetchParams.offset.toString())

  const orderByCol = fetchParams.order_by?.column ?? 'first_name'
  const orderByDirection = fetchParams.order_by?.direction ?? 'ASC'
  queryParams.append(`order_by[${orderByCol}]`, orderByDirection)

  if (fetchParams.professional_id) {
    queryParams.append("professional_id", fetchParams.professional_id.toString())
  }

  return queryParams.toString()
}

function fetchVerifiedByAdviserStats(date: DateRange, fetchParams: FetchParams): Promise<VerifiedByAdviserStats> {
  const to = formatDate(date.to, 'yyyy-MM-dd')
  const from = formatDate(date.from, 'yyyy-MM-dd')

  return apiClient.get(`/firm/professionals/verified-statistics?&to=${to}&from=${from}&${buildQueryParamsString(fetchParams)}`)
}

function fetchContentAddedOrSharedStats(date: DateRange, fetchParams: FetchParams): Promise<ContentAddedOrSharedByAdviser> {
  const to = formatDate(date.to, 'yyyy-MM-dd')
  const from = formatDate(date.from, 'yyyy-MM-dd')

  return apiClient.get(`/firm/professionals/content-added-or-shared?&to=${to}&from=${from}&${buildQueryParamsString(fetchParams)}`)
}

function createStatsQueryOptions<T>(queryKey: string, fetchFn: (date: DateRange, fetchParams: FetchParams) => Promise<T>) {
  return (date: DateRange, fetchParams: FetchParams) =>
    // eslint-disable-next-line @tanstack/query/exhaustive-deps
    queryOptions({
      queryKey: [queryKey, date, fetchParams],
      queryFn: () => fetchFn(date, fetchParams),
      placeholderData: keepPreviousData
    })
}

function useStatsQuery<T>(
  queryKeyName: string,
  statsQueryOptions: ReturnType<typeof createStatsQueryOptions<T>>,
  fetchParams: FetchParams
) {
  const { date } = useDate()
  const { data: identity } = useIdentity()

  const options = statsQueryOptions(date, fetchParams)
  options.enabled = useIsQueryEnabled(identity?.showVerifiedStatistics ?? false)

  const query = useQuery(options)
  const queryKey: Array<string | object | number | boolean | null> = [queryKeyName, date, fetchParams]
  const { resetAndRefetch } = useQueryResetWithRefresh(queryKey, query.refetch)

  return {
    data: query.data,
    isLoading: query.isLoading,
    isFetching: query.isFetching,
    isPlaceholderData: query.isPlaceholderData,
    isError: query.isError,
    isRefetchError: query.isRefetchError,
    error: query.error,
    isRefetching: query.isRefetching,
    isSuccess: query.isSuccess,
    status: query.status,
    refetch: resetAndRefetch
  }
}

export const verifiedByAdviserStatsQueryOptions = createStatsQueryOptions(QUERY_KEY_VERIFIED_BY_ADVISER_STATS, fetchVerifiedByAdviserStats)

export const useVerifiedByAdviserStats = (fetchParams: FetchParams) =>
  useStatsQuery(QUERY_KEY_VERIFIED_BY_ADVISER_STATS, verifiedByAdviserStatsQueryOptions, fetchParams)

export const contentAddedOrSharedStatsQueryOptions = createStatsQueryOptions(QUERY_KEY_CONTENT_ADDED_OR_SHARED_STATS, fetchContentAddedOrSharedStats)

export const useContentAddedOrSharedStats = (fetchParams: FetchParams) =>
  useStatsQuery(QUERY_KEY_CONTENT_ADDED_OR_SHARED_STATS, contentAddedOrSharedStatsQueryOptions, fetchParams)

const PREFETCH_RANGE = 3
export async function prefetchAroundOffset({
  queryClient,
  date,
  fetchParams,
  totalCount,
}: {
  queryClient: QueryClient;
  date: DateRange;
  fetchParams: FetchParams;
  totalCount?: number;
}) {
  const { offset, limit } = fetchParams;
  const prefetches = [];
  const shouldPrefetch = (queryKey: unknown[]) => {
    const query = queryClient.getQueryCache().find({ queryKey });
    const dataUpdatedAt = query?.state.dataUpdatedAt ?? 0;
    const staleTime = (query?.options as { staleTime?: number })?.staleTime ?? 0;
 
    return !query || Date.now() - dataUpdatedAt > staleTime;
  };

  for (let i = 1; i <= PREFETCH_RANGE; i++) {
    const nextOffset = offset + i * limit;
    const prevOffset = offset - i * limit;

    if (totalCount === undefined || nextOffset < totalCount) {
      const nextParams = { ...fetchParams, offset: nextOffset };
      const nextQueryKey = [QUERY_KEY_VERIFIED_BY_ADVISER_STATS, date, nextParams];

      if (shouldPrefetch(nextQueryKey)) {
        prefetches.push(
          queryClient.prefetchQuery(verifiedByAdviserStatsQueryOptions(date, nextParams))
        );
      }
    }

    if (prevOffset >= 0) {
      const prevParams = { ...fetchParams, offset: prevOffset };
      const prevQueryKey = [QUERY_KEY_VERIFIED_BY_ADVISER_STATS, date, prevParams];

      if (shouldPrefetch(prevQueryKey)) {
        prefetches.push(
          queryClient.prefetchQuery(verifiedByAdviserStatsQueryOptions(date, prevParams))
        );
      }
    }
  }

  await Promise.all(prefetches);
}




